import React, { useState, useEffect } from 'react';
import { Calendar, Clock, Loader2 } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle, DrawerTrigger } from './ui/drawer';
import { Button } from './ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { useIsMobile } from '../hooks/use-mobile';
import { useAxios } from './axiosContext';
import { toast } from 'sonner';
import axios from 'axios';
import { Appointment, RescheduleModalProps } from '../types/appointments';
import DatePicker from './DatePicker';


export const RescheduleModal: React.FC<RescheduleModalProps> = ({ 
  appointment, 
  currentDate, 
  currentTime, 
  onReschedule, 
  onSuccess,
  children 
}) => {
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(undefined);
  const [selectedTime, setSelectedTime] = useState<string>('');
  const [isOpen, setIsOpen] = useState(false);
  const [selectedHour, setSelectedHour] = useState<string>('');
  const [selectedMinute, setSelectedMinute] = useState<string>('');
  const [selectedPeriod, setSelectedPeriod] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [isRescheduling, setIsRescheduling] = useState(false);
  
  const isMobile = useIsMobile();
  const axiosInstance = useAxios() as typeof axios;

  // Get appointment data from props or use fallback
  const appointmentDate = appointment?.scheduledAt || currentDate;
  const appointmentTime = appointment?.scheduledAt ? 
    formatInTimeZone(parseISO(appointment.scheduledAt), 'UTC', 'h:mm a') : 
    currentTime;

  // Initialize selectedDate with appointment date if available
  useEffect(() => {
    if (appointment?.scheduledAt) {
      try {
        const parsedDate = parseISO(appointment.scheduledAt);
        setSelectedDate(parsedDate);
      } catch (error) {
        console.error('Error parsing appointment date:', error);
      }
    } else if (currentDate) {
      try {
        const parsedDate = parseISO(currentDate);
        setSelectedDate(parsedDate);
      } catch (error) {
        console.error('Error parsing current date:', error);
      }
    }
  }, [appointment?.scheduledAt, currentDate]);

  // Format appointment date for display
  const formatAppointmentDate = (dateString: string) => {
    try {
      const date = parseISO(dateString);
      return format(date, 'MMMM do');
    } catch {
      return 'Invalid date';
    }
  };

  // Initialize time dropdowns from currentTime or appointment
  useEffect(() => {
    if (appointmentTime) {
      const [time, period] = appointmentTime.split(' ');
      const [hour, minute] = time.split(':');
      
      // Ensure hour and minute match the dropdown format (with leading zeros)
      setSelectedHour(hour?.padStart(2, '0'));
      setSelectedMinute(minute?.padStart(2, '0'));
      setSelectedPeriod(period || 'AM');
    }
  }, [appointmentTime]);

  // Update selectedTime when dropdown values change
  useEffect(() => {
    if (selectedHour && selectedMinute && selectedPeriod) {
      setSelectedTime(`${selectedHour}:${selectedMinute} ${selectedPeriod}`);
    }
  }, [selectedHour, selectedMinute, selectedPeriod]);

  const generateHours = () => {
    const hours: string[] = [];
    for (let i = 1; i <= 12; i += 1) {
      hours.push(i.toString().padStart(2, '0'));
    }
    return hours;
  };

  const generateMinutes = () => {
    const minutes: string[] = [];
    for (let i = 0; i < 60; i += 10) {
      minutes.push(i.toString().padStart(2, '0'));
    }
    return minutes;
  };

  // Convert 12-hour time to 24-hour format for API
  const convertTo24Hour = (time12: string): string => {
    const [time, period] = time12.split(' ');
    const [hour, minute] = time.split(':');
    let hour24 = parseInt(hour, 10);
    
    if (period === 'PM' && hour24 !== 12) {
      hour24 += 12;
    } else if (period === 'AM' && hour24 === 12) {
      hour24 = 0;
    }
    
    return `${hour24.toString().padStart(2, '0')}:${minute}`;
  };

  // Create ISO datetime string for API
  const createISODateTime = (date: Date, time: string): string => {
    const time24 = convertTo24Hour(time);
    const dateString = format(date, 'yyyy-MM-dd');
    return `${dateString}T${time24}:00.000Z`;
  };

  /**
   * Handles the reschedule action by calling the API and updating the appointment
   */
  const handleReschedule = async () => {
    if (!selectedDate || !selectedTime) {
      toast.error('Please select both date and time');
      return;
    }

    setIsRescheduling(true);
    try {
      const newDateTime = createISODateTime(selectedDate, selectedTime);
      
      let response;
      
      if (appointment?.id) {
        // Update existing appointment
        response = await axiosInstance.put(`/calls/reschedule/${appointment.id}`, {
          scheduledAt: newDateTime,
        });
      } else if (appointment?.type) {
        // Create new appointment for intake types
        response = await axiosInstance.post('/calls', {
          scheduledAt: newDateTime,
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone, // Get user's timezone
          type: appointment.type,
        });
      } else {
        toast.error('No appointment found to reschedule');
        return;
      }

      if (response.data.success) {
        toast.success('Appointment rescheduled successfully');
        
        // Call the onReschedule callback if provided
        if (onReschedule) {
          onReschedule(format(selectedDate, 'yyyy-MM-dd'), selectedTime);
        }
        
        // Call the onSuccess callback if provided
        if (onSuccess) {
          onSuccess(response.data.data);
        }
        
        setIsOpen(false);
      } else {
        toast.error(response.data.message || 'Failed to reschedule appointment');
      }
    } catch (error: any) {
      console.error('Error rescheduling appointment:', error);
      toast.error(error.response?.data?.message || 'Failed to reschedule appointment');
    } finally {
      setIsRescheduling(false);
    }
  };

  const modalContent = (
    <div className="space-y-6">
      {/* Current appointment info */}
      {appointmentDate && appointmentTime && (
        <div className="p-3 bg-muted rounded-lg">
          <p className="text-sm text-muted-foreground">Current appointment:</p>
          <p className="font-medium">
            {appointment?.scheduledAt ? 
              `${formatAppointmentDate(appointment.scheduledAt)} at ${appointmentTime}` :
              `July 13th at ${appointmentTime}`
            }
          </p>
        </div>
      )}

      {/* Date selection with DatePicker component */}
      <div className="space-y-3">
        <h3 className="font-medium flex items-center gap-2">
          <Calendar className="h-4 w-4" />
          Select New Date
        </h3>
        <DatePicker
          value={selectedDate}
          onChange={setSelectedDate}
          placeholder="Choose a date"
          minDate={new Date()}
          isMobile={isMobile}
        />
      </div>

      {/* Time selection */}
      <div className="space-y-3">
        <h3 className="font-medium flex items-center gap-2">
          <Clock className="h-4 w-4" />
          Select New Time
        </h3>
        <div className="grid grid-cols-3 gap-3">
          <div>
            <label className="text-xs text-muted-foreground mb-1 block">Hour</label>
            <Select value={selectedHour} onValueChange={setSelectedHour}>
              <SelectTrigger className="h-9">
                <SelectValue placeholder="--" />
              </SelectTrigger>
              <SelectContent className="bg-background border shadow-lg z-50">
                {generateHours().map((hour) => (
                  <SelectItem key={hour} value={hour}>
                    {hour}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs text-muted-foreground mb-1 block">Min</label>
            <Select value={selectedMinute} onValueChange={setSelectedMinute}>
              <SelectTrigger className="h-9">
                <SelectValue placeholder="--" />
              </SelectTrigger>
              <SelectContent className="bg-background border shadow-lg z-50">
                {generateMinutes().map((minute) => (
                  <SelectItem key={minute} value={minute}>
                    {minute}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs text-muted-foreground mb-1 block">Period</label>
            <div className="relative bg-muted rounded-full h-10 flex">
              {/* Sliding thumb */}
              <div
                className={`
        absolute top-1 bottom-1 w-1/2 bg-primary rounded-full
        transform transition-transform duration-500 ease-in-out
        ${selectedPeriod === 'AM' ? 'translate-x-0' : 'translate-x-full'}
      `}
              />

              {/* Click areas */}
              {['AM', 'PM'].map((period) => (
                <button
                  key={period}
                  type="button"
                  onClick={() => setSelectedPeriod(period)}
                  className="relative flex-1 z-10 flex items-center justify-center focus:outline-none"
                >
                  <span
                    className={`
            transition-colors duration-300 ease-in-out text-xs font-semibold
            ${selectedPeriod === period
                        ? 'text-primary-foreground'
                        : 'text-muted-foreground hover:text-foreground'}
          `}
                  >
                    {period}
                  </span>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Action buttons */}
      <div className="flex gap-2 pt-4">
        <Button 
          variant="outline" 
          className="flex-1" 
          onClick={() => setIsOpen(false)}
          disabled={isRescheduling}
        >
          Cancel
        </Button>
        <Button 
          className="flex-1" 
          disabled={!selectedDate || !selectedTime || isRescheduling} 
          onClick={handleReschedule}
        >
          {isRescheduling ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Rescheduling...
            </>
          ) : (
            'Reschedule'
          )}
        </Button>
      </div>
    </div>
  );

  if (isMobile) {
    return (
      <Drawer open={isOpen} onOpenChange={setIsOpen}>
        <DrawerTrigger asChild>{children}</DrawerTrigger>
        <DrawerContent>
          <DrawerHeader>
            <DrawerTitle className="flex items-center gap-2">Reschedule Appointment</DrawerTitle>
          </DrawerHeader>
          <div className="px-4 pb-4">{modalContent}</div>
        </DrawerContent>
      </Drawer>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent className="max-w-sm w-full mx-4 max-h-[90vh] overflow-y-auto rounded-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5" />
            Reschedule Appointment
          </DialogTitle>
        </DialogHeader>
        {modalContent}
      </DialogContent>
    </Dialog>
  );
};
